/**
 * คุณภาพอากาศ (air quality) card for the result sheet — v0.9.11.
 *
 * Data: /api/air-quality, which proxies Open-Meteo's Air Quality API (powered by CAMS, the
 * Copernicus Atmosphere Monitoring Service — the same dataset behind the aerosol/dust globe maps
 * shared online). Every value shown comes straight from that response with its source + time;
 * nothing is invented. US AQI is used because it is the 0–500 scale Thai air-quality apps display.
 */
import { cachedFetch } from "./net-cache";

export interface AirCurrent {
  time: string | null;
  usAqi: number | null; pm25: number | null; pm10: number | null;
  dust: number | null; aod: number | null;
  ozone: number | null; no2: number | null; so2: number | null; co: number | null;
}
export interface AirOutlookDay { date: string; usAqiMax: number | null; pm25Max: number | null }
export interface AirData { current: AirCurrent | null; outlook: AirOutlookDay[]; model: string | null; source: string; updatedAt: string | null }

export async function loadAirQuality(lat: number, lon: number): Promise<AirData | null> {
  try {
    const r = await cachedFetch(`/api/air-quality?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`);
    if (!r.ok) return null;
    const d = (await r.json()) as AirData;
    if (!d.current && !(d.outlook?.length)) return null; // backend said "no-value"
    return d;
  } catch { return null; }
}

/** US AQI category — standard EPA bands (labels factual, not invented). */
export function aqiLevel(aqi: number): { word: string; color: string } {
  if (aqi <= 50) return { word: "ดี", color: "#22e39b" };
  if (aqi <= 100) return { word: "ปานกลาง", color: "#ffd93d" };
  if (aqi <= 150) return { word: "เริ่มมีผลต่อกลุ่มเสี่ยง", color: "#ffb020" };
  if (aqi <= 200) return { word: "มีผลต่อสุขภาพ", color: "#ff3b5c" };
  if (aqi <= 300) return { word: "มีผลต่อสุขภาพมาก", color: "#a855f7" };
  return { word: "อันตราย", color: "#9c1a2b" };
}

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
const thTime = (iso: string): string =>
  new Date(iso).toLocaleString("th-TH", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" });

/** Build the air-quality card, or null when there is nothing real to show (→ host stays empty/hidden). */
export function airQualityCard(d: AirData): HTMLElement | null {
  const c = d.current;
  const lines: string[] = [];

  // headline pill: US AQI + its category word; falls back to a PM2.5-only line if AQI is missing
  let head = "";
  if (c?.usAqi != null) {
    const lv = aqiLevel(c.usAqi);
    head = `<div class="aq-main"><span class="aq-pill" style="background:${lv.color}">AQI ${c.usAqi}</span>`
      + `<span class="aq-level">${esc(lv.word)}</span><span class="muted small">(US AQI)</span></div>`;
  }

  if (c?.pm25 != null) lines.push(`PM2.5 <b>${c.pm25}</b> µg/m³`);
  if (c?.pm10 != null) lines.push(`PM10 <b>${c.pm10}</b> µg/m³`);
  // aerosol optical depth = how much haze/dust/smoke dims sunlight — the quantity the globe maps colour
  if (c?.aod != null) lines.push(`ความขุ่นละอองลอย (AOD) <b>${c.aod}</b>`);
  if (c?.dust != null && c.dust > 0) lines.push(`ฝุ่นทราย <b>${c.dust}</b> µg/m³`);

  if (!head && !lines.length && !d.outlook.length) return null;

  // 3-day outlook: daily-max AQI shown as a range
  let outlook = "";
  const aqiMaxes = d.outlook.map((o) => o.usAqiMax).filter((v): v is number => v != null);
  if (aqiMaxes.length) {
    const lo = Math.min(...aqiMaxes), hi = Math.max(...aqiMaxes);
    const worst = aqiLevel(hi);
    outlook = `<div class="aq-outlook muted small">แนวโน้ม ${d.outlook.length} วัน (ค่าสูงสุดรายวัน): `
      + `AQI ${lo === hi ? hi : `${lo}–${hi}`} · สูงสุดระดับ “${esc(worst.word)}”</div>`;
  }

  const card = document.createElement("div");
  card.className = "rf-card aq-card";
  const upd = d.updatedAt ?? c?.time ?? null;
  card.innerHTML = `<div class="rf-head">🌫️ คุณภาพอากาศตอนนี้</div>`
    + head
    + (lines.length ? `<div class="aq-lines">${lines.map((l) => `<span class="aq-chip">${l}</span>`).join("")}</div>` : "")
    + outlook
    + `<div class="muted small aq-foot">ที่มา: ${esc(d.source)}${upd ? ` · อัปเดต ${esc(thTime(upd))}` : ""}</div>`;
  return card;
}
